// Zod DTOs for `GET /v1/teacher/dashboard/summary` (FU-01, TEACHER/ADMIN
// dashboard summary tile).
//
// NOTE: the header comment previously calling this shape "frozen" is STALE —
// Phase-3 enrollment has since shipped (`Enrollment`/`EnrollmentStatus`) and
// this DTO was extended additively for T6 to back it with real stats. Fields
// are extended, never removed/retyped, without checking `apps/api-client`
// codegen + every consumer first.
//
// `examBoards` / `subjects` are REAL aggregate counts (queried from the
// existing ExamBoard/Subject tables; `subjects` is UNFILTERED, matching
// `AnalyticsService.getOverview`'s `prisma.subject.count()` convention).
// `mediaAssets` is still an honest `0` placeholder — Phase-2 media doesn't
// exist yet, never fabricated. `recentActivity` is now populated from the 5
// most-recently-updated Resources (see `TeacherDashboardService.getSummary`).
//
// T6 additions (all additive, all real counts):
// - `subjectsPublished` / `subjectsDraft` — Subject's OWN `isPublished` flag,
//   `archivedAt: null` (archived subjects count toward NEITHER bucket). This
//   is NOT full ancestor-chain live-to-student status — Level/ExamBoard
//   publish state is a separate concern, not reflected here.
// - `students` — DISTINCT students with a currently-ACTIVE enrollment
//   (`EnrollmentStatus.ACTIVE` only; excludes EXPIRED/REVOKED/
//   PENDING_PAYMENT). Counts distinct students, not enrollment rows — a
//   student enrolled in 2 levels counts once. Platform-wide (TEACHER has
//   global curriculum read scope per ADR-001 #3), not per-subject.
// - `resourcesPublished` / `resourcesDraft` — Resource's OWN `isPublished`
//   flag only. Does NOT account for whether the resource's ancestor chain
//   (tab/section/subject/level/examBoard) is also live/published — that is a
//   materially different, more expensive computation this endpoint
//   deliberately does not attempt (see `./access.ts`'s `isPublicChainVisible`
//   doc comment — a single-section point-check, not bulk-aggregate-friendly).
//
// Consumed by `apps/api`'s route handler/validator and the
// `@hono/zod-openapi` document generator, the single source of truth for
// both.
import { z } from 'zod';

// ─── GET /v1/teacher/dashboard/summary ──────────────────────────────────

export const activityItemSchema = z.object({
  id: z.string(),
  type: z.string(),
  message: z.string(),
  occurredAt: z.string().datetime(),
});
export type ActivityItem = z.infer<typeof activityItemSchema>;

export const teacherDashboardSummaryResponseSchema = z.object({
  examBoards: z.number().int().nonnegative(),
  subjects: z.number().int().nonnegative(),
  // T6 — Subject's own `isPublished` flag, `archivedAt: null` (excludes
  // archived from both buckets).
  subjectsPublished: z.number().int().nonnegative(),
  subjectsDraft: z.number().int().nonnegative(),
  // T6 — distinct students with a currently-ACTIVE enrollment, platform-wide.
  // Field name kept as `students` (pre-existing DTO field) for backward
  // compatibility; semantics documented above/in the service.
  students: z.number().int().nonnegative(),
  // T6 — Resource's own `isPublished` flag only (see header comment).
  resourcesPublished: z.number().int().nonnegative(),
  resourcesDraft: z.number().int().nonnegative(),
  // Honest `0` placeholder — Phase-2 media doesn't exist yet.
  mediaAssets: z.number().int().nonnegative(),
  recentActivity: z.array(activityItemSchema),
});
export type TeacherDashboardSummaryResponse = z.infer<typeof teacherDashboardSummaryResponseSchema>;
